import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ENUM_LABELS, type OrderRequestRow } from '#shared/types';
import { Banner } from '#client/components/ui/Banner';
import { FormActions } from '#client/components/ui/FormActions';
import { Page } from '#client/components/ui/Page';
import { PageLoading, PageMessage } from '#client/components/ui/PageStatus';
import { useFormValues } from '#client/hooks/useFormValues';
import { useMutation } from '#client/hooks/useMutation';
import { useResourceItem } from '#client/hooks/useResourceItem';
import { RESOURCE, patientJustRegistered, routes } from '#client/lib/routes';
import { PatientBillingFields, prefillFrom, usePatientLookup } from '#client/features/patients';
import {
  buildRegisterBody,
  countUnmatchedLines,
  initialRegisterValues,
  listBookableLines,
  previewRegisterPricing,
  toggleTestId,
} from './register-request.logic';
import { NO_ERRORS, RegisterPatientCard, RequestTestsCard } from './RegisterRequestSections';
import { registerFromRequest } from './order-request.service';
import { ButtonLink } from '#client/components/ui/ButtonLink';

/**
 * Turning a request into a real visit.
 *
 * Deliberately **not** the whole registration form. The fields that are missing
 * — CNIC, marital status, panels — are things the receptionist fills in at the
 * desk with the patient in front of them, and pre-filling them from a fax would
 * put guesses into a billing record.
 *
 * The tests are ticked rather than assumed: the doctor may have asked for
 * something the lab does not do, and the registration is what the patient is
 * actually charged for.
 */
export function RegisterRequestPage() {
  const { id } = useParams<{ id: string }>();
  const request = useResourceItem<OrderRequestRow>(RESOURCE.orderRequests, id);

  if (request.loading) return <PageLoading title="Register from a request" />;
  if (!request.data) {
    return (
      <PageMessage title="Test request not found">
        {request.error ?? 'It may have been dealt with while this page was open.'}
      </PageMessage>
    );
  }

  return <Form request={request.data} />;
}

function Form({ request }: { request: OrderRequestRow }) {
  const navigate = useNavigate();
  const registration = useMutation('That registration did not go through.');
  const [validationError, setValidationError] = useState<string | null>(null);

  const bookable = listBookableLines(request.lines);
  const [chosenTestIds, setChosenTestIds] = useState<ReadonlySet<number>>(
    () => new Set(bookable.map((line) => line.test.id)),
  );

  const { values, patch, setValues } = useFormValues(initialRegisterValues(request));

  const returning = usePatientLookup(values.phone_number ?? '', true, (match) =>
    setValues((current) => prefillFrom(current, match.last_visit)),
  );

  const pricing = previewRegisterPricing(values, bookable, chosenTestIds);

  async function handleSubmit() {
    if (chosenTestIds.size === 0) {
      registration.reset();
      setValidationError('Choose at least one test.');
      return;
    }

    setValidationError(null);
    const outcome = await registration.run(() =>
      registerFromRequest(request.id, buildRegisterBody(values, chosenTestIds)),
    );
    // Same landing as the counter's form: a visit registered from a fax
    // needs its slip printed just as much as one registered at the desk.
    if (outcome.ok) void navigate(patientJustRegistered(outcome.value.id));
  }

  const errorMessage = validationError ?? registration.errorMessage;

  return (
    <Page
      title={`Register ${request.patient_name}`}
      subtitle={`From request ${request.number}`}
      backAction={
        <ButtonLink variant="secondary" size="md" to={routes.orderRequest(request.id)}>
          Back to the request
        </ButtonLink>
      }
      fullWidth
    >
      <form
        className="c-form-stack"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void handleSubmit();
        }}
      >
        {errorMessage && <Banner tone="critical">{errorMessage}</Banner>}

        {request.status !== 'pending' && (
          <Banner tone="warning">
            This request is already {ENUM_LABELS.orderRequestStatus[request.status].toLowerCase()}.
            Registering it again will be refused.
          </Banner>
        )}

        <RequestTestsCard
          bookable={bookable}
          chosenTestIds={chosenTestIds}
          unmatchedCount={countUnmatchedLines(request.lines)}
          onToggle={(testId, isChosen) =>
            setChosenTestIds((current) => toggleTestId(current, testId, isChosen))
          }
        />
        <RegisterPatientCard values={values} onFieldsChange={patch} returning={returning} />
        <PatientBillingFields
          values={values}
          onFieldsChange={patch}
          errors={NO_ERRORS}
          pricing={pricing}
          alreadyPaid={0}
        />

        <FormActions
          cancelTo={routes.orderRequest(request.id)}
          submitLabel="Register and open the patient"
          submitting={registration.isPending}
          submitSize="lg"
        />
      </form>
    </Page>
  );
}
